import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, Key, Globe, Hash, ChevronLeft, Check, Clock, Eye, EyeOff, Plus, X, Mail, KeyRound, Link2, Loader2, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import participantService from '../../services/participantService';
import studyService from '../../services/studyService';
import siteService from '../../services/siteService';
import caregiverService, { getActiveSponsorId } from '../../services/caregiverService';
import AddCaregiverModal from '../Caregivers/AddCaregiverModal';
import CaregiverSetupModal from '../Caregivers/CaregiverSetupModal';
import MaskedTextInput from '../ui/MaskedPasswordInput'
import {
  CAREGIVER_PENDING_ENROLL_TITLE,
  CAREGIVER_PENDING_ENROLL_BODY,
  shouldNotifyCaregiverPending,
} from './caregiverPending';

// Required field marker component for consistency
const RequiredFieldMarker = () => (
  <span className="text-red-500 ml-1" aria-label="required field">*</span>
);

// US timezones with their common abbreviations
const US_TIMEZONES = [
  { id: "America/New_York", label: "Eastern Time (ET)" },
  { id: "America/Chicago", label: "Central Time (CT)" },
  { id: "America/Denver", label: "Mountain Time (MT)" },
  { id: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { id: "America/Anchorage", label: "Alaska Time (AKT)" },
  { id: "America/Adak", label: "Hawaii-Aleutian Time (HST)" },
  { id: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { id: "America/Phoenix", label: "Arizona Time (MST, no DST)" },
];

// Helper function to get timezone label from ID
const getTimezoneLabel = (timezoneId) => {
  const timezone = US_TIMEZONES.find(tz => tz.id === timezoneId);
  return timezone ? timezone.label : timezoneId;
};

const AddParticipantForm = () => {
  const { studyID, siteId } = useParams();
  const navigate = useNavigate();
  const [participantData, setParticipantData] = useState({
    participant_id: '',
    pin: '',
    language: '',
    is_active: true,
    timezone: ''
  });
  // FECOA-1319: optional caregiver assignment as part of participant creation.
  // When the SC ticks "Assign a Caregiver", they queue 1+ caregivers to be
  // created/linked atomically after the participant is created.
  const [assignCaregiver, setAssignCaregiver] = useState(false);
  const [studySupportsCaregivers, setStudySupportsCaregivers] = useState(false);
  // FECOA-2156: caregivers sign in with the study's code, so the setup card shows it.
  const [studyCode, setStudyCode] = useState(null);
  const [pendingCaregivers, setPendingCaregivers] = useState([]);
  const [caregiverModalOpen, setCaregiverModalOpen] = useState(false);
  // FECOA-1428/1430: after creating in-clinic caregivers, hold their setup
  // credentials (QR/ID/PIN) to show in the success modal. Navigation to the
  // participant is deferred until the SC closes this modal.
  const [setupModal, setSetupModal] = useState(null); // { caregivers: [...], sponsorId, destination }

  const [errors, setErrors] = useState({});
  const [languages, setLanguages] = useState([]);
  const [timezones, setTimezones] = useState(US_TIMEZONES);
  const [isLoading, setIsLoading] = useState(true);
  const [siteTimezone, setSiteTimezone] = useState('');
  const [siteDetails, setSiteDetails] = useState(null);
  const [studyPartRange, setStudyPartRange] = useState(["", ""]);
  const [showPin, setShowPin] = useState(false);

  // Fetch available languages from the study
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setIsLoading(true);
        const studyLanguages = await studyService.getStudyLanguages(studyID);
        setLanguages(studyLanguages);
        
        // Set default language to English if available
        const defaultLanguage = studyLanguages.find(lang => lang.code === 'en');
        if (defaultLanguage) {
          setParticipantData(prevData => ({
            ...prevData,
            language: defaultLanguage.code
          }));
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching languages:', error);
        toast.error('Failed to load language options. Using English as default.');
        setLanguages([{ code: 'en', name: 'English' }]);
        setParticipantData(prevData => ({
          ...prevData,
          language: 'en'
        }));
        setIsLoading(false);
      }
    };

    const fetchParticipantRange = async () => {
      try {
        setIsLoading(true);
        const studyPartRange = await studyService.getStudyMinMaxParticipantId(studyID);
        setStudyPartRange(studyPartRange)
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching participant range:', error);
        toast.error('Error fetching participant range.');
        setIsLoading(false);
      }
    };



    fetchLanguages();
    fetchParticipantRange();
    // FECOA-1332: surface "Assign a Caregiver" only when the study allows it
    studyService.getSingleStudy(studyID).then((s) => {
      setStudySupportsCaregivers(!!s?.allow_caregivers);
      setStudyCode(s?.code || null);
    }).catch(() => setStudySupportsCaregivers(false));
  }, [studyID]);

  // Fetch site details to get timezone
  useEffect(() => {
    let isMounted = true;
    const fetchSiteDetails = async () => {
      try {
        const response = await siteService.getSiteDetailsForStudy(studyID, siteId);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setSiteDetails(response);
          
          // Log the site details to see the structure
          console.log('Site details loaded:', response);
          
          if (response.timezone) {
            setSiteTimezone(response.timezone);
            setParticipantData(prevData => ({
              ...prevData,
              timezone: response.timezone
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching site details:', error);
        toast.error('Could not fetch site timezone. Timezone will need to be set manually if required.');
      }
    };

    fetchSiteDetails();
    return () => {
      isMounted = false;
    };
  }, [studyID, siteId]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    // For participant_id and pin, only allow numeric input
    if ((name === 'participant_id' || name === 'pin') && !/^\d*$/.test(value)) {
      return; // Don't update state if non-numeric characters are entered
    }
    
    setParticipantData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const togglePinVisibility = () => {
    setShowPin(!showPin);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!participantData.participant_id.trim()) {
      newErrors.participant_id = 'Participant ID is required';
    } else if (!/^\d+$/.test(participantData.participant_id)) {
      newErrors.participant_id = 'Participant ID must contain only numbers';
    }

    if (studyPartRange[0] !== "" && studyPartRange[1] !== "") {
      const pin = parseInt(participantData.participant_id)
      const min = parseInt(studyPartRange[0]);
      const max = parseInt(studyPartRange[1]);
      if (pin < min || pin > max) {
        newErrors.participant_id = `Participant ID must between ${studyPartRange[0]} and ${studyPartRange[1]}`;
      }
    }

    if (!participantData.pin.trim()) {
      newErrors.pin = 'PIN is required';
    } else if (participantData.pin.trim().length < 6) {
      newErrors.pin = 'PIN must be at least 6 digits long';
    } else if (!/^\d+$/.test(participantData.pin)) {
      newErrors.pin = 'PIN must contain only numbers';
    } else if (/^(\d)\1+$/.test(participantData.pin)) {
      newErrors.pin = 'PIN cannot consist of repeated digits (e.g., 000000)';
    } else if (/^(?:012345|123456|234567|345678|456789|567890)$/.test(participantData.pin)) {
      newErrors.pin = 'PIN cannot be a simple sequence (e.g., 123456)';
    } else if (participantData.pin === participantData.participant_id) {
      newErrors.pin = 'PIN cannot be the same as the Participant ID';
    }
    if (!participantData.language.trim()) {
      newErrors.language = 'Language is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formSubmitted = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  // ---- Caregiver queue helpers (FECOA-1319) ----

  const handleQueueCaregiver = (item) => {
    setPendingCaregivers((prev) => [...prev, item]);
  };

  const removePendingCaregiver = (index) => {
    setPendingCaregivers((prev) => prev.filter((_, i) => i !== index));
  };

  const renderPendingChip = (item, index) => {
    const isLink = item.kind === 'link';
    const cg = isLink ? item.caregiver : null;
    const summary = isLink
      ? {
          name: cg.full_name || `${cg.first_name || ''} ${cg.last_name || ''}`.trim() || cg.caregiver_id,
          subtitle: `${item.relationship_type} • ${cg.caregiver_id}`,
          detail: null,
          icon: Link2,
          container: 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10',
          iconColor: 'text-emerald-600 dark:text-emerald-300',
          badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200',
          tag: 'Link existing',
        }
      : {
          name: item.summary.full_name || 'New caregiver',
          subtitle:
            item.summary.invitation_method === 'email'
              ? `${item.summary.relationship_type} • Email invite`
              : `${item.summary.relationship_type} • Temp PIN`,
          detail: item.summary.invitation_method === 'email' ? item.summary.email || null : null,
          icon: item.summary.invitation_method === 'email' ? Mail : KeyRound,
          container: 'border-sky-200 bg-sky-50 dark:border-sky-500/30 dark:bg-sky-500/10',
          iconColor: 'text-sky-600 dark:text-sky-300',
          badge: 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200',
          tag: 'Create new',
        };
    const Icon = summary.icon;
    return (
      <div
        key={index}
        className={`flex items-start justify-between gap-3 px-3 py-2 rounded-md border ${summary.container}`}
      >
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${summary.iconColor}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{summary.name}</p>
              <span className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ${summary.badge}`}>
                {summary.tag}
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{summary.subtitle}</p>
            {summary.detail && (
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{summary.detail}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => removePendingCaregiver(index)}
          className="text-gray-400 hover:text-gray-700 dark:hover:text-white flex-shrink-0"
          aria-label="Remove caregiver"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const processCaregiverQueue = async (newParticipantId) => {
    const linked = [];
    const created = [];
    const failed = [];
    for (const item of pendingCaregivers) {
      try {
        if (item.kind === 'link') {
          await caregiverService.linkToParticipant(item.caregiver.id, {
            participant_id: newParticipantId,
            site_id: siteId,
            relationship_type: item.relationship_type,
            informed_consent_verified: true,
          });
          linked.push(item);
        } else {
          // Caller fills in the participant_id and site_id at dispatch time.
          const payload = {
            ...item.payload,
            participant_id: newParticipantId,
            site_id: siteId,
          };
          const result = await caregiverService.create(payload);
          // Capture in-clinic setup credentials so the success modal can show
          // the QR/ID/PIN. Email-invite caregivers get a link by email instead,
          // so they carry no temp PIN to share here (FECOA-1428/1430).
          created.push({
            ...item,
            caregiverId: result?.caregiver_id,
            tempPin: result?.temporary_pin || null,
            name: item.summary?.full_name || null,
            relationship: item.summary?.relationship_type || null,
          });
        }
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          'Failed to add caregiver.';
        failed.push({ item, error: detail });
        // Surface each failure individually so the SC sees what didn't take.
        toast.error(detail, { duration: 7000 });
      }
    }
    return { linked, created, failed };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formSubmitted.current == true)
       return;

    formSubmitted.current = true;

    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly.');
      formSubmitted.current = false;
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('Generating schedules and survey windows…');

    try {
      // The participant's system email is generated server-side from the
      // participant's own UUID so it is globally unique. It is never exposed to
      // anyone; don't build it on the client.
      const response = await participantService.addParticipant(studyID, siteId, participantData);

      // FECOA-1319: process the queued caregivers in order.
      // Prefer the UUID id over the human-readable participant_id so URL/API
      // lookups always hit the unique PK and never collide with legacy data.
      const newParticipantId =
        response?.id || response?.participant?.id || response?.participant_id || null;
      const haveQueue = assignCaregiver && pendingCaregivers.length > 0 && newParticipantId && studySupportsCaregivers;
      let caregiverSummary = null;
      let results = null;
      if (haveQueue) {
        setSubmitStatus('Adding caregivers…');
        results = await processCaregiverQueue(newParticipantId);
        const addedCount = results.linked.length + results.created.length;
        if (results.failed.length === 0) {
          caregiverSummary = { type: 'success', message: `Added ${addedCount} caregiver(s).` };
        } else {
          caregiverSummary = {
            type: 'error',
            message: `${results.failed.length} caregiver(s) failed to add. See the Caregivers tab for details.`,
          };
        }
      }

      const baseMessage = response?.generation_warning
        ? `Participant added. ${response.generation_warning}`
        : 'Participant added successfully.';
      const combined = caregiverSummary
        ? `${baseMessage} ${caregiverSummary.message}`
        : baseMessage;
      const toastOpts = { id: 'participant-success', duration: 5000, position: 'top-center' };
      if (caregiverSummary?.type === 'error') {
        toast.error(combined, toastOpts);
      } else {
        toast.success(combined, toastOpts);
      }

      // FECOA-2038: enrollment is allowed without a caregiver, but caregiver
      // schedules stay empty until one is linked.
      const caregiversAdded = results ? results.linked.length + results.created.length : 0;
      if (shouldNotifyCaregiverPending(response, caregiversAdded)) {
        toast(`${CAREGIVER_PENDING_ENROLL_TITLE}. ${CAREGIVER_PENDING_ENROLL_BODY}`, {
          id: 'caregiver-surveys-pending',
          duration: 8000,
          position: 'top-center',
          icon: <Info className="w-5 h-5 text-primary-500 shrink-0" />,
        });
      }

      // FECOA-1428/1430: if any in-clinic caregivers were created, show the
      // setup-credentials modal first. The QR/ID/PIN are only shareable now —
      // the temp PIN is hashed server-side and can't be re-displayed later.
      const setupCaregivers = haveQueue && results
        ? (results.created || [])
            .filter((c) => c.caregiverId && c.tempPin)
            .map((c) => ({
              caregiverId: c.caregiverId,
              tempPin: c.tempPin,
              name: c.name,
              relationship: c.relationship,
            }))
        : [];

      const caregiversDestination = `/studies/${studyID}/sites/${siteId}/participant/${newParticipantId}/#caregivers`;

      if (setupCaregivers.length > 0) {
        setSetupModal({
          caregivers: setupCaregivers,
          sponsorId: getActiveSponsorId(),
          destination: caregiversDestination,
        });
      } else if (haveQueue) {
        navigate(caregiversDestination);
      } else if (assignCaregiver && newParticipantId && studySupportsCaregivers) {
        navigate(`/studies/${studyID}/sites/${siteId}/participant/${newParticipantId}/?addCaregiver=1#caregivers`);
      } else {
        navigate(`/studies/${studyID}/sites/${siteId}`);
      }
    } catch (error) {
      console.error('Error adding participant: ', error);
      toast.error(error.response?.data?.error || 'An error occurred while adding the participant.');
      formSubmitted.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {isSubmitting && (
        <div
          aria-busy="true"
          aria-live="polite"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-950/70"
        >
          <Loader2 className="w-10 h-10 text-primary-400 animate-spin" />
          <p className="mt-4 text-white text-base font-medium">Adding participant…</p>
          <p className="mt-1 text-gray-300 text-sm">{submitStatus || 'Generating schedules and survey windows. This usually takes a few seconds.'}</p>
        </div>
      )}
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="mb-1"
        >
          <Link
            to={`/studies/${studyID}/sites/${siteId}`}
            className="back-link"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back to Site Details</span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="card overflow-hidden"
        >
          <div className="card-band">
            <h2 className="card-band-title">Add New Participant</h2>
          </div>
          <div className="p-6">
            <div className="mb-5 text-sm text-gray-500 dark:text-gray-400">
              Fields marked with <RequiredFieldMarker /> are required
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="participant_id" className="label">
                  Participant ID<RequiredFieldMarker />
                </label>
                <div className="relative">
                  <Hash className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    id="participant_id"
                    name="participant_id"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    className={`input pl-10 pr-3 ${errors.participant_id ? 'input-error' : ''}`}
                    placeholder="Enter participant ID (numbers only)"
                    value={participantData.participant_id}
                    onChange={handleInputChange}
                  />
                </div>
                {errors.participant_id && (
                  <p className="field-error">{errors.participant_id}</p>
                )}
              </div>

              <div>
                <label htmlFor="pin" className="label">
                  PIN<RequiredFieldMarker />
                </label>
                <div className="relative">
                  <Key className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <MaskedTextInput
                    id="pin"
                    name="pin"
                    show={showPin}
                    minLength={6}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    className={`input pl-10 pr-10 ${errors.pin ? 'input-error' : ''}`}
                    placeholder="Enter 6+ digit PIN (numbers only)"
                    value={participantData.pin}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    onClick={togglePinVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label={showPin ? "Hide PIN" : "Show PIN"}
                  >
                    {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.pin && (
                  <p className="field-error">{errors.pin}</p>
                )}
              </div>

              <div>
                <label htmlFor="language" className="label">
                  Language<RequiredFieldMarker />
                </label>
                <div className="relative">
                  <Globe className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <select
                    id="language"
                    name="language"
                    required
                    className={`input pl-10 pr-9 appearance-none ${errors.language ? 'input-error' : ''}`}
                    value={participantData.language}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  >
                    <option value="" disabled>
                      {isLoading ? 'Loading languages...' : 'Select language'}
                    </option>
                    {languages.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                {errors.language && (
                  <p className="field-error">{errors.language}</p>
                )}
              </div>

              {/* Timezone override field */}
              <div>
                <label htmlFor="timezone" className="label">
                  Timezone Override
                </label>
                <div className="relative">
                  <Clock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <select
                    id="timezone"
                    name="timezone"
                    className={`input pl-10 pr-9 appearance-none ${errors.timezone ? 'input-error' : ''}`}
                    value={participantData.timezone}
                    onChange={handleInputChange}
                  >
                    <option value="">Use Site Timezone ({getTimezoneLabel(siteTimezone) || 'None Set'})</option>
                    {timezones.map((timezone) => (
                      <option key={timezone.id} value={timezone.id}>
                        {timezone.label}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                <p className="hint">
                  By default, participant will use the site's timezone. Only override if this participant needs a different timezone.
                </p>
                {errors.timezone && (
                  <p className="field-error">{errors.timezone}</p>
                )}
              </div>

              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      id="is_active"
                      name="is_active"
                      type="checkbox"
                      checked={participantData.is_active}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors duration-150 ${participantData.is_active ? 'border-primary-600 bg-primary-600 dark:border-primary-500 dark:bg-primary-500' : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'}`}>
                      <Check className={`h-3.5 w-3.5 text-white transition-opacity duration-150 ${participantData.is_active ? 'opacity-100' : 'opacity-0'}`} strokeWidth={3} />
                    </div>
                  </div>
                  <span className="ml-2.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                    Active
                  </span>
                </label>
              </div>

              {/* FECOA-1319: Assign a Caregiver — only surfaced when the study allows it.
                  SC queues 1+ caregivers here; on submit, the participant is created
                  first, then each queued caregiver is linked or created in turn. */}
              {studySupportsCaregivers && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={assignCaregiver}
                      onChange={(e) => {
                        setAssignCaregiver(e.target.checked);
                        if (!e.target.checked) setPendingCaregivers([]);
                      }}
                      className="mt-0.5 h-4 w-4 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        Assign a Caregiver
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Queue one or more caregivers below. They&rsquo;ll be linked or created when you click Add Participant.
                      </p>
                    </div>
                  </label>

                  {assignCaregiver && (
                    <div className="mt-4 space-y-2">
                      {pendingCaregivers.map((item, index) => renderPendingChip(item, index))}
                      <button
                        type="button"
                        onClick={() => setCaregiverModalOpen(true)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-primary-500/60 bg-white px-3 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 dark:bg-transparent dark:text-primary-400 dark:hover:bg-primary-500/10"
                      >
                        <Plus className="h-4 w-4" />
                        {pendingCaregivers.length === 0 ? 'Add Caregiver' : 'Add Another Caregiver'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {caregiverModalOpen && (
                <AddCaregiverModal
                  deferred
                  onQueue={handleQueueCaregiver}
                  onClose={() => setCaregiverModalOpen(false)}
                />
              )}

              {setupModal && (
                <CaregiverSetupModal
                  caregivers={setupModal.caregivers}
                  studyCode={studyCode}
                  sponsorId={setupModal.sponsorId}
                  onClose={() => {
                    const dest = setupModal.destination;
                    setSetupModal(null);
                    if (dest) navigate(dest);
                  }}
                />
              )}

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-6 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => navigate(`/studies/${studyID}/sites/${siteId}`)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isSubmitting ? 'Adding participant…' : 'Add Participant'}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AddParticipantForm;
